// One building, in the job page's right-hand pane (docs/mockups/buildings-ipad.html §3):
// its name, whether it is one building or a type repeated N times, notes, the
// buildings that differ from it, its place in the list and Remove. A building
// that differs from its type has a name, notes and its own add-on areas; it is
// one of the type's N and always counts 1.
import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Label } from '@higgins/ui';
import { buildingCount, buildingName, canAddDiffering, lowestTypeCount, typeIdOf, type BuildingGroup } from '../lib/buildings';
import { patchFields, type DeviceRecord } from '../lib/device-db';
import type { DeferredSave } from '../lib/deferred-save';
import { useDeferredSave } from '../lib/use-deferred-save';
import { AddAction } from './add-action';
import { ConflictChoices } from './conflict-choices';
import { ErrorMessage } from './message';
import { NumberField } from './number-field';
import { RemoveBuilding } from './remove-building';
import { SavedTextField } from './saved-text-field';

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

interface Props {
  building: DeviceRecord;
  /** The job's records, for its type, the buildings that differ from it and whether it may go. */
  records: readonly DeviceRecord[];
  /** Its group in the list when it is a top-level building (null for one that differs from a type). */
  group: BuildingGroup | null;
  /** Where it sits among the top-level buildings, for Move up / Move down. */
  place: { first: boolean; last: boolean };
  onMove: (direction: -1 | 1) => Promise<void>;
  onAddArea: () => Promise<void>;
  onAddDiffering: () => Promise<void>;
  onRemove: () => Promise<void>;
}

/** A count waiting to be saved, and the building it's for. */
interface CountChange {
  id: string;
  count: number;
}

/** The count box: whole numbers from `lowest` up; anything else waits, unsaved, with a hint. */
function CountField({ building, lowest, saver }: { building: DeviceRecord; lowest: number; saver: DeferredSave<CountChange> }) {
  const [problem, setProblem] = useState<string | null>(null);

  function change(value: number | null) {
    if (value === null || !Number.isInteger(value) || value < lowest) {
      setProblem(`A whole number, ${lowest} or more.`);
      return;
    }
    setProblem(null);
    saver.schedule({ id: building.id, count: value });
  }

  return (
    <div>
      <Label htmlFor="building-count">How many?</Label>
      <div className="mt-1 w-28">
        <NumberField value={buildingCount(building)} onChange={change} label="How many buildings of this type" />
      </div>
      {problem && <p className="mt-1 text-xs text-red-700">{problem}</p>}
    </div>
  );
}

export function BuildingEditor({ building, records, group, place, onMove, onAddArea, onAddDiffering, onRemove }: Props) {
  const [actionError, setActionError] = useState<string | null>(null);
  // The count waits a moment after typing (its box is a number, not a SavedTextField); a conflict's choice keeps or drops it.
  const countSaver = useDeferredSave((change: CountChange) => patchFields(change.id, { hig_count: change.count }));
  useEffect(() => () => void countSaver.flush(), [building.id]); // eslint-disable-line react-hooks/exhaustive-deps -- save what's waiting when another building is chosen
  const typeId = typeIdOf(building);
  const type = typeId ? records.find((r) => r.table === 'building' && r.id.toLowerCase() === typeId) : undefined;
  const differing = group?.differing.length ?? 0;
  const count = buildingCount(building);
  const isType = !type && count > 1;
  const name = buildingName(building);

  useEffect(() => setActionError(null), [building.id]);

  /**
   * Before a conflict is resolved: a count still waiting is saved (sending mine) or dropped (taking CRM's).
   * Text boxes save as they lose focus, which tapping the choice does first; taking CRM's then replaces them.
   */
  async function settleTyping(keep: boolean) {
    if (keep) await countSaver.flush();
    else countSaver.cancel();
  }

  const saveField = (column: string) => (value: string) => patchFields(building.id, { [column]: value === '' && column !== 'hig_name' ? null : value });

  /** One building ↔ a type: a type starts at 2 (or the buildings that already differ from it). */
  const setKind = (repeated: boolean) => patchFields(building.id, { hig_count: repeated ? Math.max(count, lowestTypeCount(differing)) : 1 });

  async function run(action: () => Promise<void>) {
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      {building.state === 'conflict' && !building.refusedReason && <ConflictChoices area={building} onResolve={settleTyping} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          {type ? `One of ${buildingName(type)} × ${buildingCount(type)}` : isType ? `A building type × ${count}` : 'One building'}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {group && (
            <>
              <button type="button" onClick={() => void run(() => onMove(-1))} disabled={place.first} className="flex min-h-11 items-center gap-1 text-sm text-slate-600 disabled:opacity-40">
                <ArrowUp className="h-4 w-4" /> Move up
              </button>
              <button type="button" onClick={() => void run(() => onMove(1))} disabled={place.last} className="flex min-h-11 items-center gap-1 text-sm text-slate-600 disabled:opacity-40">
                <ArrowDown className="h-4 w-4" /> Move down
              </button>
            </>
          )}
          <RemoveBuilding building={building} records={records} onRemove={onRemove} onError={setActionError} />
        </div>
      </div>
      {actionError && <ErrorMessage>{actionError}</ErrorMessage>}

      <div>
        <Label htmlFor="building-name">Name</Label>
        <SavedTextField id="building-name" label="Building name" value={name} onSave={saveField('hig_name')} className="mt-1" />
      </div>

      {type ? (
        <p className="text-sm text-slate-600">
          It is one of the {buildingCount(type)}, not extra. Add here only what it has that {buildingName(type)} doesn’t (a rear deck): those areas are priced once, on
          top of the {buildingCount(type)}.
        </p>
      ) : (
        <>
          <div>
            <Label>What is it?</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void setKind(false)}
                disabled={differing > 0}
                className={`min-h-11 rounded-md border px-3 text-sm disabled:opacity-40 ${!isType ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 bg-white text-slate-700'}`}
              >
                One building
              </button>
              <button
                type="button"
                onClick={() => void setKind(true)}
                className={`min-h-11 rounded-md border px-3 text-sm ${isType ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 bg-white text-slate-700'}`}
              >
                A building type, repeated
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {isType
                ? `Measure one; everything in its areas is priced × ${count} — surfaces, sundries, wash, scaffold moves, hire and other trades.`
                : 'Measured as it is. For something repeated inside one area (Balcony × 24), use a repeat group on the surfaces instead.'}
            </p>
          </div>
          {isType && <CountField building={building} lowest={lowestTypeCount(differing)} saver={countSaver} />}
        </>
      )}

      <div>
        <Label htmlFor="building-notes">Notes</Label>
        <SavedTextField id="building-notes" label="Building notes" value={text(building.fields.hig_notes)} onSave={saveField('hig_notes')} className="mt-1" rows={2} />
      </div>

      {isType && (
        <div>
          <Label>One that’s different</Label>
          <p className="mt-1 text-sm text-slate-600">
            {differing
              ? `${group!.differing.map((d) => buildingName(d.building)).join(', ')} ${differing === 1 ? 'differs' : 'differ'} from the rest.`
              : `When one of the ${count} has something the others don’t (a rear deck), add it here.`}
          </p>
          <AddAction onClick={() => void run(onAddDiffering)} disabled={!canAddDiffering(building, differing)}>
            One of these differs…
          </AddAction>
        </div>
      )}

      <AddAction onClick={() => void run(onAddArea)}>{type ? `Area only ${name} has` : `Area in ${name}`}</AddAction>
    </div>
  );
}
